import { useDisableMfa } from '@hooks/useMfa';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MfaDisableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MfaDisableDialog({ open, onOpenChange }: MfaDisableDialogProps) {
  const disableMutation = useDisableMfa();

  const handleDisable = () => {
    disableMutation.mutate(undefined, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      disableMutation.reset();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
          <DialogDescription>
            This will remove the extra security layer from your account. You&apos;ll need to set up
            MFA again if you want to re-enable it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {disableMutation.isError && (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">Failed to Disable MFA</p>
                  <p className="mt-1">
                    {getErrorMessage(disableMutation.error) || 'Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable}
              disabled={disableMutation.isPending}
            >
              {disableMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable MFA'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
